import { useEffect, useMemo } from 'react';
// @mui
import { CssBaseline } from '@mui/material';
import {
  createTheme,
  StyledEngineProvider,
  ThemeOptions,
  ThemeProvider as MUIThemeProvider,
} from '@mui/material/styles';

// components
import { useSettingsContext } from '../components/settings';

import customShadows from './customShadows';
import GlobalStyles from './globalStyles';
import componentsOverride from './overrides';
//
import palette from './palette';
import shadows from './shadows';
import typography from './typography';

// ----------------------------------------------------------------------

// MUI Theme 확장: 커스텀 z-index 레벨 추가
declare module '@mui/material/styles' {
  interface ZIndex {
    drawerSettings: number;
    drawerSummary: number;
  }
}

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function ThemeProvider({ children }: Props) {
  const { themeMode, themeDirection } = useSettingsContext();

  // theme 이 변경될때마다 document에 dark class toggle
  useEffect(() => {
    const isDarkMode = themeMode === 'dark';
    document.body.classList.toggle('dark', isDarkMode);
  }, [themeMode]);

  const themeOptions: ThemeOptions = useMemo(
    () => ({
      palette: palette(themeMode),
      typography,
      shape: { borderRadius: 4 },
      direction: themeDirection,
      shadows: shadows(themeMode),
      customShadows: customShadows(themeMode),
      zIndex: {
        // MUI 기본 z-index: drawer(1200), modal(1300), snackbar(1400), tooltip(1500)
        // 커스텀 Drawer들은 MUI drawer(1200)와 modal(1300) 사이에 배치
        drawerSettings: 1270, // UtilsDial icons & setting drawer
        drawerSummary: 1280, // 현황 요약 drawer
      },
    }),
    [themeDirection, themeMode],
  );

  const theme = createTheme(themeOptions);

  theme.components = componentsOverride(theme);

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
}
